import {
	SceneTransform,
	Settings,
	LeftPanelTab,
	Scaling,
	LearningCenterState,
	GoldTooltipState,
	SilverTooltipState,
} from 'redux/mode/reducers';
import { Command } from 'redux/mode/commands';
import { Quality } from 'dragonmouse-shared/interfaces';

// Data that should persist across browser sessions and be shared across tabs
export interface LocalData {
	transforms?: { [sceneId: string]: SceneTransform };
	leftPanelWidths?: { [tab in LeftPanelTab]: number };
	rightPanelWidth?: number;
	settings?: Settings;
	prototypeScaling?: Scaling;
	learningCenterState?: LearningCenterState;
	chatQuality?: Quality;
	sidebarVisible?: boolean;
	sidebarVisibilityInitialized?: boolean;
	goldTooltipState?: GoldTooltipState;
	silverTooltipState?: SilverTooltipState;
}

// Data that should be per-tab and not persist across browser sessions
export interface SessionData {
	activeChatId?: string | null;
	activeLeftPanelTab?: LeftPanelTab;
	commandStack?: Command[];
	undoneCommands?: Command[];
	centeredFrameId?: string | null;
	trackedUserIds?: string[];
	panelsVisible?: boolean;
}

// Core idea here is that storing stuff long-term can be handled here, since there
// is some interplay between parts of this. Could switch more stuff to this pattern,
// which allows us to do a simple get / set when using it and change the way it's
// stored without changing the rest of the code.
class Storage {
	conduitInitialized: boolean = false;
	sceneFramesRetrieved: boolean = false;

	get local(): LocalData {
		const localData: LocalData = {};

		const jsonText = localStorage.getItem('mode');
		if (jsonText) {
			const saved = JSON.parse(jsonText);
			if (typeof saved === 'object') {
				// TODO: consider using lodash's merge to make this more compact - https://lodash.com/docs/4.17.15#merge
				// since merge doesn't seem to deal with the Number.isFinite cases above, I'm not adding it just yet...
				if (saved.transforms !== undefined) {
					localData.transforms = { ...saved.transforms };
				}
				if (saved.leftPanelWidths !== undefined) {
					localData.leftPanelWidths = { ...saved.leftPanelWidths };
				}
				if (saved.rightPanelWidth !== undefined) {
					localData.rightPanelWidth = saved.rightPanelWidth;
				}
				if (saved.settings !== undefined) {
					localData.settings = { ...saved.settings };
				}
				if (saved.prototypeScaling !== undefined) {
					localData.prototypeScaling = saved.prototypeScaling;
				}
				if (saved.learningCenterState !== undefined) {
					localData.learningCenterState = { ...saved.learningCenterState };
				}
				if (saved.chatQuality !== undefined) {
					localData.chatQuality = saved.chatQuality;
				}
				if (saved.sidebarVisible !== undefined) {
					localData.sidebarVisible = saved.sidebarVisible;
				}
				if (saved.sidebarVisibilityInitialized !== undefined) {
					localData.sidebarVisibilityInitialized = saved.sidebarVisibilityInitialized;
				}
				if (saved.goldTooltipState !== undefined) {
					localData.goldTooltipState = saved.goldTooltipState;
				}
				if (saved.silverTooltipState !== undefined) {
					localData.silverTooltipState = saved.silverTooltipState;
				}
			}
		}
		return localData;
	}

	set local(data: LocalData) {
		localStorage.setItem('mode', JSON.stringify(data));
	}

	get session(): SessionData {
		const sessionData: SessionData = {};

		const jsonText = sessionStorage.getItem('mode');
		if (jsonText) {
			const saved = JSON.parse(jsonText);
			if (typeof saved === 'object') {
				if (saved.activeChatId !== undefined) {
					sessionData.activeChatId = saved.activeChatId;
				}
				if (saved.activeLeftPanelTab !== undefined) {
					sessionData.activeLeftPanelTab = saved.activeLeftPanelTab;
				}
				if (saved.commandStack !== undefined) {
					sessionData.commandStack = [...saved.commandStack];
				}
				if (saved.undoneCommands !== undefined) {
					sessionData.undoneCommands = [...saved.undoneCommands];
				}
				if (saved.centeredFrameId !== undefined) {
					sessionData.centeredFrameId = saved.centeredFrameId;
				}
				if (saved.trackedUserIds !== undefined) {
					sessionData.trackedUserIds = [...saved.trackedUserIds];
				}
				if (saved.panelsVisible !== undefined) {
					sessionData.panelsVisible = saved.panelsVisible;
				}
			}
		}
		return sessionData;
	}

	set session(data: SessionData) {
		sessionStorage.setItem('mode', JSON.stringify(data));
	}
}

export default new Storage();
